"use client";

import Image from "next/image";
import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { AedSymbol } from "@/components/ui/AedSymbol";
import { cn } from "@/lib/utils";
import {
  ShieldCheck,
  Landmark,
  BadgeCheck,
  Lock,
  Fingerprint,
  Truck,
  Zap,
  RefreshCw,
} from "lucide-react";

type CardId =
  | "starter"
  | "rates"
  | "vault"
  | "shariah"
  | "delivery"
  | "security";

// ── Motion ───────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Card shell ───────────────────────────────────────────────────────────────

function FeatureCard({
  id,
  activeId,
  className,
  children,
  onActivate,
  radius = "rounded-card-sm",
}: {
  id: CardId;
  activeId: CardId;
  className?: string;
  children: React.ReactNode;
  onActivate: (id: CardId) => void;
  radius?: string;
}) {
  const active = activeId === id;
  return (
    <motion.button
      type="button"
      variants={fadeUp(false)}
      onClick={() => onActivate(id)}
      onFocus={() => onActivate(id)}
      onMouseEnter={() => onActivate(id)}
      aria-pressed={active}
      className={cn(
        "group relative block w-full appearance-none overflow-hidden border border-[#d4e4fa] p-0 text-left [font:inherit] transition-all duration-300",
        radius,
        "focus-visible:outline-none",
        active
          ? "border-primary-300"
          : "hover:border-primary-200",
        className
      )}
    >
      {children}
    </motion.button>
  );
}

// ── Card copy block ──────────────────────────────────────────────────────────

function CardCopy({
  title,
  children,
  size = "sm",
  className,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "lg";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative z-20 flex flex-col gap-1 not-italic",
        size === "lg" ? "p-[35px]" : "p-[23px]",
        className
      )}
    >
      <h3
        className={cn(
          "font-bold leading-none tracking-[-0.01em] text-text-heading",
          size === "lg" ? "text-[28px]" : "text-[20px]"
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          "text-text-body leading-[1.2]",
          size === "lg" ? "text-base" : "text-sm"
        )}
      >
        {children}
      </p>
    </div>
  );
}

// ── Rate chart inline (small card on top middle) ────────────────────────────

function RateChartCard() {
  return (
    <div className="relative z-10 mx-[23px] mt-[110px] rounded-[10px] border border-[#d4e4fa] bg-white p-3">
      {/* Header: label + price + change */}
      <div className="flex items-end justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <p className="text-[10px] font-medium uppercase tracking-[0.06em] text-text-muted">
            24K Gold / gm
          </p>
          <p className="text-[18px] font-bold leading-none text-text-heading tabular-nums">
            <span className="inline-flex items-baseline gap-1">
              <AedSymbol className="h-[0.7em] w-[0.7em]" label="AED " />
              450.20
            </span>
          </p>
        </div>
        <span className="inline-flex items-center gap-0.5 rounded-full bg-success-bg px-1.5 py-0.5 text-[9px] font-semibold leading-none text-success">
          <svg
            viewBox="0 0 8 8"
            className="h-2 w-2"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M4 1.5L7 6H1L4 1.5z" />
          </svg>
          1.8%
        </span>
      </div>

      {/* Chart */}
      <svg
        viewBox="0 0 285 70"
        preserveAspectRatio="none"
        className="mt-2.5 h-[58px] w-full overflow-visible"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="rateFillGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#d89a2c" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#d89a2c" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0 56 C14 46 24 50 36 44 C52 34 62 40 78 38 C96 34 106 44 124 38 C142 32 150 38 168 32 C188 26 194 30 210 22 C228 12 238 18 254 10 C268 4 276 6 285 2 L285 70 L0 70 Z"
          fill="url(#rateFillGrad)"
        />
        <path
          d="M0 56 C14 46 24 50 36 44 C52 34 62 40 78 38 C96 34 106 44 124 38 C142 32 150 38 168 32 C188 26 194 30 210 22 C228 12 238 18 254 10 C268 4 276 6 285 2"
          fill="none"
          stroke="#d89a2c"
          strokeLinecap="round"
          strokeWidth="1.5"
        />
        {/* End dot */}
        <circle cx="283" cy="3" r="2.5" fill="#d89a2c" />
        <circle
          cx="283"
          cy="3"
          r="5"
          fill="#d89a2c"
          fillOpacity="0.18"
        />
      </svg>

      {/* Period tabs */}
      <div className="mt-2 flex items-center gap-1 rounded-[6px] bg-[#f3f6fb] p-0.5">
        {["1D", "1W", "1M", "1Y"].map((period, i) => (
          <span
            key={period}
            className={cn(
              "flex-1 rounded-[4px] px-1 py-1 text-center text-[10px] font-semibold leading-none transition-colors",
              i === 0
                ? "bg-white text-text-heading shadow-[0_1px_2px_rgba(14,58,138,0.08)]"
                : "text-text-muted"
            )}
          >
            {period}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────────────────────

export function FeaturesSection() {
  const reduce = useReducedMotion() ?? false;
  const [activeId, setActiveId] = useState<CardId>("starter");

  return (
    <motion.section
      className="relative overflow-hidden bg-neutral-0"
      aria-labelledby="features-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-neutral-0 via-primary-50/25 to-neutral-0" />

      <div className="relative mx-auto max-w-[1440px] px-6 pb-16 pt-16 sm:px-10 sm:pb-20 sm:pt-20 lg:px-20 lg:pb-24 lg:pt-24">
        {/* Header */}
        <motion.div variants={fadeUp(reduce)} className="mb-10 max-w-[720px]">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-primary-400">
            Why JustGold
          </p>
          <h2
            id="features-heading"
            className="scroll-mt-28 text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900 xl:whitespace-nowrap"
          >
            Six reasons this is safer than a drawer at home
          </h2>
        </motion.div>

        {/* Bento grid — matches Figma 60:8839 layout */}
        <motion.div
          variants={container}
          className="grid gap-6 lg:grid-cols-[428fr_841fr]"
        >
          {/* ── LEFT: Starts from AED 10 (tall card) ──────────────────── */}
          <FeatureCard
            id="starter"
            activeId={activeId}
            onActivate={setActiveId}
            radius="rounded-card"
            className="min-h-[560px] bg-[#f7f9fe] lg:h-[647px]"
          >
            {/* Decorative background pattern */}
            <Image
              src="/bento-bg-pattern-blue.png"
              alt=""
              fill
              sizes="428px"
              className="pointer-events-none select-none object-cover opacity-90"
              aria-hidden="true"
            />

            <CardCopy
              size="lg"
              className="absolute inset-x-0 top-0"
              title="Buy & sell in seconds"
            >
              Move whenever you like, no lock-in, no penalty for
              selling early.
            </CardCopy>

            <Image
              src="/bento-phone.png"
              alt="JustGold mobile app showing gold balance and purchase actions"
              width={1328}
              height={2726}
              sizes="(max-width: 1024px) 220px, 240px"
              className={cn(
                "absolute bottom-4 left-8 z-10 h-[400px] w-auto object-contain transition-transform duration-500 sm:left-10 sm:h-[440px] lg:bottom-6 lg:left-10 lg:h-[473px]",
                activeId === "starter" && "scale-[1.02]"
              )}
              priority
            />

            {/* Right-side benefit chips (vertical column) */}
            <div className="absolute right-4 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-3 lg:right-5">
              {[
                { icon: Zap, label: "Instant\nPurchase" },
                { icon: RefreshCw, label: "No Lock-in\nEver" },
                { icon: ShieldCheck, label: "Transparent\nPricing" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex w-[96px] flex-col items-center gap-2 rounded-card-sm
                    border border-white/80 bg-white/65 px-3 py-3.5 backdrop-blur-md
                    shadow-[0_4px_18px_rgba(14,58,138,0.08)]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50">
                    <Icon className="h-4 w-4 text-primary-600" strokeWidth={2} />
                  </span>
                  <p className="text-center text-[12px] font-semibold leading-[1.2] text-text-heading">
                    {label.split("\n").map((line, i) => (
                      <span key={i} className="block">
                        {line}
                      </span>
                    ))}
                  </p>
                </div>
              ))}
            </div>
          </FeatureCard>

          {/* ── RIGHT: 2 rows ─────────────────────────────────────────── */}
          <div className="grid gap-6">
            {/* Top row: Rates chart + Vault */}
            <div className="grid gap-6 lg:grid-cols-[353fr_464fr]">
              {/* Live Market Rates (chart) */}
              <FeatureCard
                id="rates"
                activeId={activeId}
                onActivate={setActiveId}
                className="h-[308px] bg-[#f8fafd]"
              >
                <Image
                  src="/bento-bg-pattern-light.png"
                  alt=""
                  fill
                  sizes="353px"
                  className="pointer-events-none select-none object-cover"
                  aria-hidden="true"
                />
                <CardCopy
                  className="absolute inset-x-0 top-0"
                  title="The real Dubai rate"
                >
                  Live international gold and silver market rates.
                </CardCopy>
                <RateChartCard />
              </FeatureCard>

              {/* Vaulted & Insured */}
              <FeatureCard
                id="vault"
                activeId={activeId}
                onActivate={setActiveId}
                className="h-[308px] bg-gradient-to-b from-[#fafafc] to-[#f6f9fe]"
              >
                <CardCopy
                  className="absolute inset-x-0 top-0"
                  title="Held by Loomis, insured in Dubai"
                >
                  Held in an insured Dubai vault, audited
                  independently. Never on our balance sheet.
                </CardCopy>

                {/* Glass chips listing trust points */}
                <div className="absolute bottom-[60px] left-[23px] z-20 flex flex-col gap-2">
                  {[
                    { icon: ShieldCheck, label: "Fully insured custody" },
                    { icon: Landmark, label: "Dubai based vaults" },
                    { icon: BadgeCheck, label: "Independently audited" },
                  ].map(({ icon: Icon, label }) => (
                    <span
                      key={label}
                      className="inline-flex w-fit items-center gap-2 rounded-full
                        border border-white/80 bg-white/70 px-3 py-1.5 backdrop-blur-md
                        shadow-[0_2px_8px_rgba(14,58,138,0.06)]"
                    >
                      <Icon
                        className="h-3.5 w-3.5 text-primary-600"
                        strokeWidth={2}
                      />
                      <span className="text-[12px] font-medium text-text-heading">
                        {label}
                      </span>
                    </span>
                  ))}
                </div>

                <Image
                  src="/bento-vault-figma.png"
                  alt="Insured vault with gold bars"
                  width={640}
                  height={640}
                  sizes="464px"
                  className={cn(
                    "absolute -right-4 -bottom-6 z-10 h-[340px] w-auto translate-x-[14%] object-contain transition-transform duration-500",
                    activeId === "vault" && "scale-[1.03]"
                  )}
                />
              </FeatureCard>
            </div>

            {/* Bottom row: 3 equal cards */}
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Shariah-Compliant */}
              <FeatureCard
                id="shariah"
                activeId={activeId}
                onActivate={setActiveId}
                className="h-[308px] bg-[#f9f9fe]"
              >
                <CardCopy
                  className="absolute inset-x-0 top-0"
                  title="Certified Shariah compliant"
                >
                  Real physical gold, no interest, no lending.
                </CardCopy>

                <Image
                  src="/bento-shariah-coin.png"
                  alt="Shariah-compliant gold coin certification"
                  width={420}
                  height={420}
                  sizes="264px"
                  className={cn(
                    "absolute left-1/2 top-[100px] z-10 h-[180px] w-auto -translate-x-1/2 object-contain transition-transform duration-500",
                    activeId === "shariah" && "scale-[1.04]"
                  )}
                />

                {/* Glass chips listing Shariah credentials */}
                <div className="absolute bottom-[16px] left-1/2 z-20 flex -translate-x-1/2 flex-row items-center gap-1.5">
                  {[
                    { icon: BadgeCheck, label: "Scholar approved" },
                  ].map(({ icon: Icon, label }) => (
                    <span
                      key={label}
                      className="inline-flex w-fit items-center gap-2 rounded-full
                        border border-white/80 bg-white/70 px-3.5 py-1.5 backdrop-blur-md
                        shadow-[0_2px_8px_rgba(14,58,138,0.06)]"
                    >
                      <Icon
                        className="h-4 w-4 text-primary-600"
                        strokeWidth={2}
                      />
                      <span className="whitespace-nowrap text-[12px] font-medium text-text-heading">
                        {label}
                      </span>
                    </span>
                  ))}
                </div>
              </FeatureCard>

              {/* Physical Gold Delivery */}
              <FeatureCard
                id="delivery"
                activeId={activeId}
                onActivate={setActiveId}
                className="h-[308px] bg-[#f9f9fe]"
              >
                {/* full bleed background image */}
                <Image
                  src="/bento-delivery-box.png"
                  alt="Boxed physical gold delivery"
                  fill
                  sizes="264px"
                  className="pointer-events-none select-none object-cover"
                />
                {/* top fade so title is readable */}
                <div
                  className="absolute inset-x-0 top-0 z-10 h-[121px] bg-gradient-to-b from-[#f9f9fe] via-[#f9f9fe]/70 to-transparent"
                  aria-hidden="true"
                />
                <CardCopy
                  className="absolute inset-x-0 top-0 z-20"
                  title="Take it home whenever"
                >
                  Turn your grams into coins or bars, delivered
                  anywhere in the UAE.
                </CardCopy>

                {/* Glass chip — delivery proof */}
                <div className="absolute bottom-[16px] left-1/2 z-20 flex -translate-x-1/2">
                  <span
                    className="inline-flex w-fit items-center gap-2 rounded-full
                      border border-white/80 bg-white/70 px-3.5 py-1.5 backdrop-blur-md
                      shadow-[0_2px_8px_rgba(14,58,138,0.06)]"
                  >
                    <Truck
                      className="h-4 w-4 text-primary-600"
                      strokeWidth={2}
                    />
                    <span className="whitespace-nowrap text-[12px] font-medium text-text-heading">
                      Insured UAE delivery
                    </span>
                  </span>
                </div>
              </FeatureCard>

              {/* Secure by Design */}
              <FeatureCard
                id="security"
                activeId={activeId}
                onActivate={setActiveId}
                className="h-[308px] bg-[#f5f7fd]"
              >
                <CardCopy
                  className="absolute inset-x-0 top-0"
                  title="Bank-grade security"
                >
                  Your account and data are protected the same way
                  banks protect theirs.
                </CardCopy>

                {/* Glass chips listing security features */}
                <div className="absolute bottom-[32px] left-[18px] z-20 flex flex-col gap-2">
                  {[
                    { icon: Lock, label: "256 bit encryption" },
                    { icon: BadgeCheck, label: "ISO 27001 certified" },
                    { icon: Fingerprint, label: "Biometric login" },
                  ].map(({ icon: Icon, label }) => (
                    <span
                      key={label}
                      className="inline-flex w-fit items-center gap-1.5 rounded-full
                        border border-white/80 bg-white/70 px-2.5 py-1 backdrop-blur-md
                        shadow-[0_2px_8px_rgba(14,58,138,0.06)]"
                    >
                      <Icon
                        className="h-3 w-3 text-primary-600"
                        strokeWidth={2}
                      />
                      <span className="text-[11px] font-medium text-text-heading">
                        {label}
                      </span>
                    </span>
                  ))}
                </div>

                <Image
                  src="/bento-shield-secure.png"
                  alt="Secure shield with lock"
                  width={440}
                  height={512}
                  sizes="264px"
                  className={cn(
                    "absolute right-2 bottom-2 z-10 h-[200px] w-auto object-contain transition-transform duration-500",
                    activeId === "security" && "scale-[1.04]"
                  )}
                />
              </FeatureCard>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}
